import React, { useState, useRef, useEffect } from 'react';
import CodeEntryPanel from './CodeEntryPanel';
import ApproveOnPhone from './ApproveOnPhone';
import StepLayout from './StepLayout';

// RESET-CODE-WAIT (2026-11-03, thread takeover-reset-code-wait): on the
// password-reset code prompt only (input_request.purpose ===
// 'password_reset'), tell the seller where the email may have landed and how
// long we keep waiting. Since 10-27, 38 reset-code waits timed out against 9
// that succeeded, with the seller still on the page in ~88% of the timeouts.
// No resend: the /2fa/resend endpoint is a stub.
function formatRemaining(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const systemNow = () => Date.now();

export function ResetCodeHelp({ expiresAt, now = systemNow }) {
  const deadline = expiresAt ? Date.parse(expiresAt) : NaN;
  const [remaining, setRemaining] = useState(() => (Number.isFinite(deadline) ? deadline - now() : null));
  useEffect(() => {
    if (!Number.isFinite(deadline)) return undefined;
    const id = setInterval(() => setRemaining(deadline - now()), 1000);
    return () => clearInterval(id);
  }, [deadline, now]);

  return (
    <div className="reset-code-help" data-testid="reset-code-help">
      <p>
        Can&rsquo;t see it? Check your <strong>Spam</strong>, <strong>Junk</strong> and{' '}
        <strong>Promotions</strong> folders for an email from LinkedIn.
      </p>
      {remaining !== null && (
        <p className="reset-code-help__timer" data-testid="reset-code-countdown">
          {remaining > 0
            ? <>Enter it within <strong>{formatRemaining(remaining)}</strong></>
            : 'Still waiting for the code…'}
        </p>
      )}
    </div>
  );
}

// PR 4 — generic challenge surface keyed off the BE state envelope's
// `input_request` object:
//
//   input_request = {
//     challenge_id, type, label, destination_hint, attempt, expires_at
//   }
//
// type values handled here:
//   - 'code'                       → 6-digit OTP entry (CodeEntryPanel variant='otp')
//   - 'two_factor'                 → 6-digit OTP entry, same UI as 'code' (copy may differ)
//   - 'credentials'                → re-render credential form with "didn't work" banner
//   - 'app_approval'               → ApproveOnPhone (no input, no button)
//   - 'captcha_or_manual_review'   → "Our team is reviewing" copy
//
// The component is stateless w.r.t. polling — the parent (App.jsx) drives
// the state envelope, and a `challenge_id` change re-mounts this component
// via React's key={challengeId} discipline so the prior input doesn't leak
// across challenge swaps.
//
// `onSubmit(challengeId, type, value)` is the single submit channel. The
// parent wires it to api.submitChallenge. On reject (stale challenge), the
// parent should bump pollNonce on useSessionState to re-poll for the
// freshest envelope; this component itself doesn't talk to the network.

export default function GenericChallenge({
  inputRequest,
  onSubmit,
  isReconnecting,
}) {
  const [code, setCode] = useState('');
  const [credsEmail, setCredsEmail] = useState('');
  const [credsPassword, setCredsPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const codeInputRef = useRef(null);

  if (!inputRequest) return null;
  const { challenge_id: challengeId, type, label, destination_hint: destination, attempt } = inputRequest;

  const handleCodeComplete = (entered) => {
    setCode(entered);
  };

  const submitCode = async () => {
    if (code.length < 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(challengeId, type, { code });
      // Parent advances on the next state-envelope poll; we stay
      // mounted with a "verifying…" indicator until that happens.
    } catch (err) {
      setError(err?.message || 'Submission failed.');
      setSubmitting(false);
    }
  };

  const submitCredentials = async () => {
    if (!credsEmail.trim() || !credsPassword) {
      setError('Email and password are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(challengeId, type, {
        linkedinEmail: credsEmail.trim(),
        password: credsPassword,
      });
    } catch (err) {
      setError(err?.message || 'Submission failed.');
      setSubmitting(false);
    }
  };

  // --- code + two_factor ---------------------------------------------------
  if (type === 'code' || type === 'two_factor') {
    const headline = label
      || (type === 'two_factor'
        ? 'Enter the code from your authenticator app or SMS'
        : 'Enter the code LinkedIn just sent');
    const supporting = destination
      ? `LinkedIn sent the code to ${destination}.`
      : 'LinkedIn sends the code via whichever method is enabled on your account.';
    const showRetryBanner = typeof attempt === 'number' && attempt > 1;
    // B-2FA-WRONG-CODE (2026-05-31): an authenticator/SMS code isn't re-sent
    // by LinkedIn, so the email-centric "LinkedIn sent a fresh one" copy is
    // wrong for the OTP case — tell the seller to enter the CURRENT code.
    const retryBannerText = type === 'two_factor'
      ? 'That code wasn’t accepted. Enter the current code from your authenticator app or SMS.'
      : 'That code didn’t work. LinkedIn sent a fresh one. Enter the latest code below.';
    return (
      <StepLayout title="Verification Code" variant="centered">
        {showRetryBanner && (
          <p style={{
            background: 'rgba(245, 158, 11, 0.12)',
            color: '#92400e',
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 16,
            textAlign: 'center',
          }}>
            {retryBannerText}
          </p>
        )}
        <p style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, textAlign: 'center', marginBottom: 8 }}>
          {headline}
        </p>
        <p style={{ color: 'var(--text2)', fontSize: 13, textAlign: 'center', marginBottom: 16 }}>
          {supporting}
        </p>
        <CodeEntryPanel
          ref={codeInputRef}
          variant={destination && destination.includes('@') ? 'email_code' : 'otp'}
          code={code}
          onCodeComplete={handleCodeComplete}
          onSubmit={submitCode}
          loading={submitting}
          verifying={submitting && !error}
          error={error}
          inboxEmail={destination && destination.includes('@') ? destination : undefined}
          compactHeader
        />
        {inputRequest.purpose === 'password_reset' && (
          <ResetCodeHelp expiresAt={inputRequest.expires_at} />
        )}
        {isReconnecting && (
          <p style={{ color: 'var(--text2)', fontSize: 12, textAlign: 'center', marginTop: 12 }}>
            Reconnecting…
          </p>
        )}
      </StepLayout>
    );
  }

  // --- app_approval --------------------------------------------------------
  if (type === 'app_approval') {
    return (
      <StepLayout title="Approve sign-in on your phone" variant="centered">
        <ApproveOnPhone
          subtitle={label || "We'll continue automatically once LinkedIn confirms your approval."}
          error={null}
        />
        {isReconnecting && (
          <p style={{ color: 'var(--text2)', fontSize: 12, textAlign: 'center', marginTop: 12 }}>
            Reconnecting…
          </p>
        )}
      </StepLayout>
    );
  }

  // --- credentials (retry) -------------------------------------------------
  if (type === 'credentials') {
    return (
      <StepLayout title="LinkedIn Credentials" variant="centered">
        <p style={{
          background: 'rgba(239, 68, 68, 0.10)',
          color: '#991b1b',
          padding: '12px 14px',
          borderRadius: 8,
          fontSize: 13,
          marginBottom: 16,
          textAlign: 'center',
        }}>
          {label || "Those login details didn’t work. Please check and try again."}
        </p>
        <form
          onSubmit={(e) => { e.preventDefault(); submitCredentials(); }}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <div>
            <label
              htmlFor="creds-retry-email"
              style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}
            >
              LinkedIn Email
            </label>
            <input
              id="creds-retry-email"
              type="email"
              autoComplete="email"
              value={credsEmail}
              onChange={(e) => setCredsEmail(e.target.value)}
              style={{ fontSize: 16, padding: '6px 12px', width: '100%' }}
            />
          </div>
          <div>
            <label
              htmlFor="creds-retry-password"
              style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}
            >
              Password
            </label>
            <input
              id="creds-retry-password"
              type="password"
              autoComplete="current-password"
              value={credsPassword}
              onChange={(e) => setCredsPassword(e.target.value)}
              style={{ fontSize: 16, padding: '6px 12px', width: '100%' }}
            />
          </div>
          {error && (
            <p style={{ color: 'var(--danger, #ef4444)', fontSize: 13, textAlign: 'center' }}>
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="primary-cta"
            style={{
              background: submitting ? 'var(--primary-soft)' : 'var(--primary)',
              color: '#fff',
              padding: 14,
              borderRadius: 8,
              border: 'none',
              fontSize: 16,
              fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Submitting…' : 'Try again'}
          </button>
        </form>
        {isReconnecting && (
          <p style={{ color: 'var(--text2)', fontSize: 12, textAlign: 'center', marginTop: 12 }}>
            Reconnecting…
          </p>
        )}
      </StepLayout>
    );
  }

  // --- captcha_or_manual_review --------------------------------------------
  if (type === 'captcha_or_manual_review') {
    // B-CAPTCHA-FE-SURFACING (2026-05-30): this passive copy is now a FALLBACK
    // only — App.jsx renders the interactive StepCaptchaViewer (screencast
    // solver) for captcha challenges and only drops to this branch if the
    // stream can't load. Keep the seller HERE (the check is solvable live) —
    // never tell them to close the tab.
    return (
      <StepLayout title="Security check in progress" variant="centered">
        <p style={{ color: 'var(--text)', fontSize: 15, textAlign: 'center', lineHeight: 1.5, marginBottom: 12 }}>
          {label || "LinkedIn is showing a quick security check. Please keep this tab open. We’re loading it for you."}
        </p>
        <p style={{ color: 'var(--text2)', fontSize: 13, textAlign: 'center', lineHeight: 1.5 }}>
          This can take a moment. If it doesn’t appear, refresh and we’ll reconnect.
        </p>
        {isReconnecting && (
          <p style={{ color: 'var(--text2)', fontSize: 12, textAlign: 'center', marginTop: 12 }}>
            Reconnecting…
          </p>
        )}
      </StepLayout>
    );
  }

  // Unknown type — show a generic placeholder rather than a hard error so
  // a future BE-side addition doesn't blow the FE up. Operator dashboard
  // will see the unknown-type warning in its own logs.
  return (
    <StepLayout title="Still working" variant="centered">
      <p style={{ color: 'var(--text)', fontSize: 14, textAlign: 'center' }}>
        Hang tight. We’ll let you know when there’s an update.
      </p>
    </StepLayout>
  );
}
